import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, RefreshCw } from 'lucide-react';
import { getUID } from '@/utils';
import axios from 'axios';
import DatePicker from '@/components/common/DatePicker/DatePicker';
import styles from './TransactionsTab.module.css';

/**
 * TransactionsTab Component
 * Displays deposit and withdrawal transactions with filters
 */
const TransactionsTab = () => {
  const [activeTab, setActiveTab] = useState('Deposit');
  const [openDropDown, setOpenDropDown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transactionData, setTransactionData] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: '',
    type: ''
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setOpenDropDown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `https://api.jackopot.com/api/${activeTab.toLowerCase()}New`,
        { params: { uid: getUID() } }
      );

      let processedData = [];

      // Handle different response formats based on the activeTab
      if (activeTab === 'Withdraw') {
        const withdrawals = response.data.withdrawals || [];
        const fiatWithdrawals = response.data.fiatWithdrawals || [];

        const formattedFiatWithdrawals = fiatWithdrawals.map(item => ({
          date: item.date,
          amount: `${item.amount} ${item.currency || ''}`,
          status: item.status,
          details: `Bank: ${item.bank_name || 'N/A'}, Acc: ${item.account_number || 'N/A'}`,
          type: 'Fiat'
        }));

        const formattedWithdrawals = withdrawals.map(item => ({
          date: item.date || item.created_at,
          amount: item.amount,
          status: item.status,
          details: item.details,
          type: 'Crypto'
        }));

        processedData = [...formattedWithdrawals, ...formattedFiatWithdrawals];
      } else {
        // For deposits
        if (Array.isArray(response.data)) {
          processedData = response.data.map(item => ({
            date: item.date || item.created_at,
            amount: item.amount,
            status: item.status,
            details: item.details,
            type: 'Crypto'
          }));
        } else {
          const deposits = response.data.deposits || [];
          const fiatDeposits = response.data.fiatDeposits || [];

          const formattedDeposits = deposits.map(item => ({
            date: item.date || item.created_at,
            amount: item.amount,
            status: item.status,
            details: item.details,
            type: 'Crypto'
          }));

          const formattedFiatDeposits = fiatDeposits.map(item => ({
            date: item.date || item.created_at,
            amount: `${item.amount} ${item.currency || ''}`,
            status: item.status,
            details: item.details,
            type: 'Fiat'
          }));

          processedData = [...formattedDeposits, ...formattedFiatDeposits];
        }
      }

      // Sort data by date in descending order (newest first)
      const sortedData = processedData.sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      });

      setTransactionData(sortedData);
    } catch (err) {
      console.error('API call failed', err);
      setError('Failed to fetch data.');
      setTransactionData([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, refreshTrigger]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const filteredData = transactionData.filter(transaction => {
    let matches = true;
    if (filters.status) {
      matches = matches && transaction.status.toUpperCase() === filters.status.toUpperCase();
    }
    if (filters.dateFrom) {
      matches = matches && new Date(transaction.date) >= new Date(filters.dateFrom);
    }
    if (filters.dateTo) {
      matches = matches && new Date(transaction.date) <= new Date(filters.dateTo);
    }
    if (filters.type) {
      matches = matches && transaction.type === filters.type;
    }
    return matches;
  });

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    switch(statusLower) {
      case 'processing':
      case 'pending':
        return 'var(--color-live)';
      case 'approved':
      case 'success':
      case 'done':
        return 'var(--color-success)';
      case 'rejected':
      case 'failed':
        return 'var(--color-error)';
      default:
        return 'var(--color-text-secondary)';
    }
  };

  return (
    <div className={styles.container}>
      {/* Top Controls */}
      <div className={styles.topControls}>
        <div className={`${styles.dropdownContainer} dropdown-container`}>
          <button
            className={styles.dropdownButton}
            onClick={(e) => {
              e.stopPropagation();
              setOpenDropDown(!openDropDown);
            }}
          >
            <span className={styles.coinName}>{activeTab}</span>
            <ChevronDown size={20} />
          </button>

          {openDropDown && (
            <div className={styles.dropdownPanel} onClick={(e) => e.stopPropagation()}>
              {['Deposit', 'Withdraw'].map((type) => (
                <div
                  key={type}
                  className={`${styles.coinOption} ${activeTab === type ? styles.coinOptionSelected : ''}`}
                  onClick={() => {
                    if (activeTab !== type) {
                      setTransactionData([]);
                      setActiveTab(type);
                      setFilters({ status: '', dateFrom: '', dateTo: '', type: '' });
                    }
                    setOpenDropDown(false);
                  }}
                >
                  <span className={styles.coinName}>{type}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <button className={styles.refreshButton} onClick={handleRefresh}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className={styles.filterContainer}>
        <div className={styles.filterGroup}>
          <label className={styles.label}>Status</label>
          <select
            className={styles.filterSelect}
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          >
            <option value="">All</option>
            {activeTab === 'Deposit' ? (
              <>
                <option value="SUCCESS">Success</option>
                <option value="PENDING">Pending</option>
                <option value="FAILED">Failed</option>
                <option value="PROCESSING">Processing</option>
                <option value="APPROVED">Approved</option>
              </>
            ) : (
              <>
                <option value="In Queue">In Queue</option>
                <option value="Processing">Processing</option>
                <option value="Done">Done</option>
                <option value="Wager Not Completed">Wager Not Completed</option>
              </>
            )}
          </select>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.label}>From Date</label>
          <DatePicker
            selected={filters.dateFrom ? new Date(filters.dateFrom) : null}
            onChange={(date) => {
              const dateString = date ? date.toISOString().split('T')[0] : '';
              setFilters(prev => ({ ...prev, dateFrom: dateString }));
            }}
            placeholderText="Select from date"
            maxDate={filters.dateTo ? new Date(filters.dateTo) : new Date()}
            dateFormat="MM/dd/yyyy"
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.label}>To Date</label>
          <DatePicker
            selected={filters.dateTo ? new Date(filters.dateTo) : null}
            onChange={(date) => {
              const dateString = date ? date.toISOString().split('T')[0] : '';
              setFilters(prev => ({ ...prev, dateTo: dateString }));
            }}
            placeholderText="Select to date"
            minDate={filters.dateFrom ? new Date(filters.dateFrom) : null}
            maxDate={new Date()}
            dateFormat="MM/dd/yyyy"
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.label}>Type</label>
          <select
            className={styles.filterSelect}
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
          >
            <option value="">All</option>
            <option value="Crypto">Crypto</option>
            <option value="Fiat">Fiat</option>
          </select>
        </div>
        <button
          className={styles.filterResetButton}
          onClick={() => setFilters({ status: '', dateFrom: '', dateTo: '', type: '' })}
        >
          Reset Filters
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loader}>
            <span className={styles.loaderText}>loading</span>
            <span className={styles.load}></span>
          </div>
        </div>
      ) : error ? (
        <div className={styles.emptyState}>
          <p>Oops! Failed to fetch data!</p>
          <button className={styles.retryButton} onClick={handleRefresh}>
            Try Again
          </button>
        </div>
      ) : filteredData.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No {activeTab.toLowerCase()} transactions found</p>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <div>Date</div>
            <div>Amount</div>
            <div>Status</div>
            <div>{activeTab === 'Deposit' ? 'Transaction' : 'Details'}</div>
          </div>

          {filteredData.map((transaction, index) => (
            <div key={index} className={styles.tableRow}>
              <div data-label="Date">{formatDateTime(transaction.date)}</div>
              <div data-label="Amount">
                {transaction.amount}
                {transaction.type && (
                  <span className={styles.typeBadge}>({transaction.type})</span>
                )}
              </div>
              <div data-label="Status">
                <span
                  className={styles.statusIndicator}
                  style={{ color: getStatusColor(transaction.status) }}
                >
                  <span className={styles.statusDot}></span>
                  {transaction.status}
                </span>
              </div>
              <div data-label={activeTab === 'Deposit' ? 'Transaction' : 'Details'} className={styles.detailsCell}>
                {activeTab === 'Deposit' ? transaction.details : 'N/A'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TransactionsTab;
